import axiosInstance from '../axiosConfig';

export interface Event {
    id: number;
    title: string;
    image: string;
    image_url: string;
    description?: string;
    date?: string;
    author?: string;
    comments?: number;
    order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface EventsResponse {
    success: boolean;
    data: Event[];
    message: string;
    count: number;
    meta?: {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
    };
}

export interface EventResponse {
    success: boolean;
    data: Event;
    message: string;
}

export const eventService = {
    // Initialize CSRF token
    async initializeCsrf() {
        try {
            await axiosInstance.get('/sanctum/csrf-cookie');
            console.log('CSRF token initialized');
        } catch (error) {
            console.error('Failed to initialize CSRF token:', error);
        }
    },

    // Get all events with pagination and search
    async getAll(params?: { page?: number; per_page?: number; search?: string }): Promise<EventsResponse> {
        try {
            const response = await axiosInstance.get('/api/admin/events', { params });
            return response.data;
        } catch (error) {
            console.error('Error in getAll:', error);
            throw error;
        }
    },

    // Get single event by ID
    async getById(id: number): Promise<EventResponse> {
        try {
            const response = await axiosInstance.get(`/api/admin/events/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in getById:', error);
            throw error;
        }
    },

    // Create new event
    async create(formData: FormData): Promise<EventResponse> {
        try {
            const response = await axiosInstance.post('/api/admin/events', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in create:', error);
            throw error;
        }
    },

    // Update event
    async update(id: number, formData: FormData): Promise<EventResponse> {
        try {
            // Add _method field to spoof PUT method for Laravel
            formData.append('_method', 'PUT');
            
            const response = await axiosInstance.post(`/api/admin/events/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in update:', error);
            throw error;
        }
    },

    // Delete event
    async delete(id: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.delete(`/api/admin/events/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in delete:', error);
            throw error;
        }
    },

    // Bulk delete events
    async bulkDelete(ids: number[]): Promise<{ success: boolean; message: string; deleted_count?: number }> {
        try {
            const response = await axiosInstance.post('/api/admin/events/bulk-delete', { ids });
            return response.data;
        } catch (error) {
            console.error('Error in bulkDelete:', error);
            throw error;
        }
    },

    // Update status
    async updateStatus(id: number, isActive: boolean): Promise<{ success: boolean; message: string; data: any }> {
        try {
            const response = await axiosInstance.patch(`/api/admin/events/${id}/status`, {
                is_active: isActive
            });
            return response.data;
        } catch (error) {
            console.error('Error in updateStatus:', error);
            throw error;
        }
    },

    // Reorder events
    async reorder(orders: { id: number; order: number }[]): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.post('/api/admin/events/reorder', { orders });
            return response.data;
        } catch (error) {
            console.error('Error in reorder:', error);
            throw error;
        }
    }
};